import type { TFunction } from 'i18next'
import { z } from 'zod'

// Note: Error messages here are fallback. The actual translated messages
// should be applied in the UI layer using useTranslation
export const getClusterRevisionFormSchema = (t: TFunction) =>
  z.object({
    name: z
      .string()
      .min(1, String(t('validation.required')))
      .min(5, String(t('newClusterForm.fields.name.validation.min')))
      .max(15, String(t('newClusterForm.fields.name.validation.max')))
      .regex(
        /^[a-z][a-z0-9-]{3,13}[a-z0-9]$/,
        String(t('newClusterForm.fields.name.validation.allowedCharacters')),
      ),
    deploymentLabel: z
      .string()
      .min(1, String(t('translation:validation.required'))),
    templateId: z.string().min(1, String(t('translation:validation.required'))),
    productId: z.number().min(1, String(t('translation:validation.required'))),
    system: z.record(z.any()),
    customer: z.record(z.any()),
    override: z.record(z.any()),
    //    Raw values contain stringified JSONs for the validation purpose
    _system_raw_json: z
      .string()
      .superRefine((value, ctx) =>
        validationSystemConfigSection(value, ctx, t),
      ),
    _override_raw_json: z.string().refine(safelyParseJsonString, {
      message: String(t('translation:validation.invalidJson')),
    }),
    _customer_raw_json: z.string().refine(safelyParseJsonString, {
      message: String(t('translation:validation.invalidJson')),
    }),
  })

export function safelyParseJsonString(str: string): object | null {
  try {
    return JSON.parse(str)
  } catch (_error) {
    return null
  }
}

function validationSystemConfigSection(
  value: string,
  ctx: z.RefinementCtx,
  t: TFunction,
) {
  const parsed = safelyParseJsonString(value)

  if (!parsed) {
    ctx.addIssue({
      message: String(t('translation:validation.invalidJson')),
      code: 'custom',
    })
    return z.never()
  }
  /* Because `name` and `deployment_label` are provided as form fields (for the purpose of
   * validation and having deployment label options) those fields should not be allowed to be
   * added inside JSON config to preserve single source of truth.*/
  if ('global' in parsed && typeof parsed.global === 'object') {
    if ('name' in (parsed.global as { name?: unknown })) {
      ctx.addIssue({
        message: String(
          t(
            'features:clusterRevisionForm.validation.nameIsNotAllowedInSystemJson',
          ),
        ),
        code: z.ZodIssueCode.custom,
      })
      return z.never()
    }
    if (
      'deployment_label' in (parsed.global as { deployment_label?: unknown })
    ) {
      ctx.addIssue({
        message: String(
          t(
            'features:clusterRevisionForm.validation.deploymentLabelIsNotAllowedInSystemJson',
          ),
        ),
        code: z.ZodIssueCode.custom,
      })
    }
  }
}
